import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Mail, Gift } from "lucide-react";

const tiers = [
  {
    id: "hourly",
    name: "Hourly Bookkeeping",
    price: "$9.99",
    unit: "/hour",
    note: "Starting at $9.99 USD per hour",
    desc: "Perfect for light or irregular bookkeeping needs. Pay only for the hours you use.",
    features: ["Transaction categorization", "Bank reconciliation", "QuickBooks / Xero support", "No minimum hours", "Weekly progress updates"],
    highlighted: false,
  },
  {
    id: "weekly",
    name: "Weekly Bookkeeping",
    price: "$320",
    unit: "/week",
    note: "40 hours of dedicated bookkeeping work",
    desc: "A full 40-hour week of professional bookkeeping at a flat, predictable rate.",
    features: ["40 hours per week", "Dedicated bookkeeper", "Daily books maintenance", "Weekly financial summary", "Priority support"],
    highlighted: false,
  },
  {
    id: "monthly-fte",
    name: "Monthly FTE Bookkeeping",
    price: "$1,499",
    unit: "/month",
    note: "Full-time equivalent dedicated bookkeeper",
    desc: "Your own full-time bookkeeper — trained on your business, working your hours.",
    features: ["Full-time dedicated staff", "Complete books ownership", "Month-end close by day 5", "AR support included", "Direct communication line", "Monthly management reports"],
    highlighted: true,
  },
  {
    id: "monthly-ap",
    name: "Full-Time AP Staff",
    price: "$2,199",
    unit: "/month",
    note: "Dedicated accounts payable specialist",
    desc: "A full-time dedicated professional managing your complete accounts payable cycle.",
    features: ["Full-time dedicated AP specialist", "Invoice processing & 3-way match", "Payment scheduling & runs", "Vendor management", "Discount capture", "Monthly AP aging reports"],
    highlighted: false,
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-[#FF5A00] text-white text-sm font-bold uppercase tracking-widest px-5 py-2.5 mb-6" data-testid="free-trial-badge">
            <Gift size={16} /> 1 Week Free Trial on Every Plan
          </div>
          <h1 className="font-heading font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl">
            Simple pricing. <span className="text-[#FF5A00]">Zero surprises.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Hourly bookkeeping starting at $9.99 USD per hour. No contracts, no setup fees, cancel anytime. All prices in USD.
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:border lg:border-border">
            {tiers.map((tier, i) => (
              <div
                key={tier.id}
                data-testid={`pricing-tier-${tier.id}`}
                className={`flex flex-col p-8 border border-border lg:border-0 hover-lift ${
                  i !== 0 ? "lg:border-l lg:border-border" : ""
                } ${tier.highlighted ? "bg-[#0A0A0A] text-white relative" : "bg-white"}`}
              >
                {tier.highlighted && (
                  <span className="absolute top-0 right-0 bg-[#FF5A00] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
                    Most Popular
                  </span>
                )}
                <h3 className="font-heading font-bold text-lg">{tier.name}</h3>
                <div className="mt-4">
                  <span className="font-mono-price text-4xl font-bold text-[#FF5A00]">{tier.price}</span>
                  <span className={`text-sm ${tier.highlighted ? "text-zinc-400" : "text-muted-foreground"}`}>{tier.unit}</span>
                </div>
                <p className={`text-xs font-mono-price mt-1 ${tier.highlighted ? "text-zinc-400" : "text-muted-foreground"}`}>{tier.note}</p>
                <p className={`text-sm mt-4 leading-relaxed ${tier.highlighted ? "text-zinc-300" : "text-muted-foreground"}`}>{tier.desc}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={16} className="text-[#FF5A00] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 space-y-3">
                  <Link
                    to="/appointment"
                    data-testid={`pricing-trial-btn-${tier.id}`}
                    className={`flex items-center justify-center gap-2 font-bold px-4 py-3.5 text-sm transition-colors ${
                      tier.highlighted
                        ? "bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                        : "bg-foreground text-white hover:bg-[#FF5A00]"
                    }`}
                  >
                    Start Free Trial <ArrowRight size={15} />
                  </Link>
                  <a
                    href={`mailto:info@coresure.us?subject=${encodeURIComponent(`Inquiry: ${tier.name} (${tier.price}${tier.unit})`)}`}
                    data-testid={`pricing-email-btn-${tier.id}`}
                    className={`flex items-center justify-center gap-2 border font-bold px-4 py-3.5 text-sm transition-colors ${
                      tier.highlighted
                        ? "border-zinc-600 text-white hover:border-[#FF5A00] hover:text-[#FF5A00]"
                        : "border-foreground hover:border-[#FF5A00] hover:text-[#FF5A00]"
                    }`}
                  >
                    Email Us <Mail size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8">
            Questions about a plan? Email us at <a href="mailto:info@coresure.us" className="font-bold text-foreground hover:text-[#FF5A00]">info@coresure.us</a>.
          </p>
        </div>
      </section>

      <section className="border-b border-border bg-muted/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-heading font-extrabold text-3xl tracking-tight text-center mb-12">Pricing FAQs</h2>
          <div className="space-y-0 border border-border bg-white">
            {[
              ["How does the 1-week free trial work?", "Book an appointment, tell us about your business, and we'll assign a bookkeeper for one full week — completely free, no credit card required. Keep working with us only if you're happy."],
              ["Are there any long-term contracts?", "No. All plans are month-to-month (or hour-to-hour). Cancel anytime with no penalties or fees."],
              ["What's the difference between Weekly and Monthly FTE?", "Weekly ($320) gives you 40 hours of bookkeeping in a given week. Monthly FTE ($1,499) gives you a permanently assigned full-time bookkeeper every month at a better effective rate."],
              ["How do I pay?", "We send a monthly invoice by email with secure USD payment options for US and Canadian businesses. Email info@coresure.us for details."],
              ["Can I combine services?", "Absolutely. Many clients pair Monthly FTE Bookkeeping with a dedicated AP specialist. We'll tailor a bundle in your free consultation."],
            ].map(([q, a], i) => (
              <div key={q} className={`p-6 ${i !== 0 ? "border-t border-border" : ""}`}>
                <h3 className="font-heading font-bold">{q}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-heading font-extrabold text-3xl tracking-tight">Start your free week today</h2>
          <Link
            to="/appointment"
            data-testid="pricing-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-10 py-4 mt-6 transition-colors"
          >
            Book Appointment <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
